import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, BookOpen, FileText, Music, Award, Clock, ArrowLeft, Lock, PlayCircle, Timer, Download, Quote } from "lucide-react";
import { useCart } from "@/context/CartContext";
import LeadMagnet from "@/components/LeadMagnet";
import { VideoEmbed } from "@/components/ProductCard";
import useMeta from "@/hooks/useMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priceUSD = (n) => `$${Number(n).toFixed(2)}`;

// Per-visitor rolling 7-day countdown for the c_conflict_3 introductory offer.
// First visit anchors a start timestamp in localStorage; when the window
// expires, we roll it forward another 7 days so every visitor always sees
// a live countdown.
const INTRO_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const INTRO_KEY = "gls_intro_window_c_conflict_3";

const useIntroCountdown = () => {
  const [remaining, setRemaining] = useState(() => {
    try {
      let start = Number(localStorage.getItem(INTRO_KEY));
      const now = Date.now();
      if (!start || Number.isNaN(start) || now - start >= INTRO_WINDOW_MS) {
        start = now;
        localStorage.setItem(INTRO_KEY, String(start));
      }
      return start + INTRO_WINDOW_MS - now;
    } catch { return INTRO_WINDOW_MS; }
  });
  useEffect(() => {
    const id = setInterval(() => {
      try {
        let start = Number(localStorage.getItem(INTRO_KEY));
        const now = Date.now();
        if (!start || now - start >= INTRO_WINDOW_MS) {
          start = now;
          localStorage.setItem(INTRO_KEY, String(start));
        }
        setRemaining(start + INTRO_WINDOW_MS - now);
      } catch {}
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const totalSec = Math.max(0, Math.floor(remaining / 1000));
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return { d, h, m, s };
};

const IntroCountdown = ({ compact = false }) => {
  const { d, h, m, s } = useIntroCountdown();
  const pad = (n) => String(n).padStart(2, "0");
  if (compact) {
    return (
      <span data-testid="intro-countdown-compact" className="inline-flex items-center gap-1.5 font-mono text-[13px] text-[#8C4A57]">
        <Timer className="w-3.5 h-3.5" />
        {d}d {pad(h)}h {pad(m)}m {pad(s)}s
      </span>
    );
  }
  return (
    <div data-testid="intro-countdown" className="inline-flex items-center gap-2 bg-[#F7E2E5] border border-[#E8DCDD] rounded-sm px-3 py-2 text-[#8C4A57]">
      <Timer className="w-4 h-4" />
      <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">Ends in</span>
      <span className="font-mono text-[13px] font-semibold" data-testid="intro-countdown-value">
        {d}d {pad(h)}h {pad(m)}m {pad(s)}s
      </span>
    </div>
  );
};

const SOCIAL_PROOF_LOGOS = [
  { src: "/assets/logo1.png", alt: "Client organization 1" },
  { src: "/assets/logo2.png", alt: "Client organization 2" },
  { src: "/assets/logo3.png", alt: "Client organization 3" },
  { src: "/assets/logo4.png", alt: "Client organization 4" },
  { src: "/assets/logo5.png", alt: "Client organization 5" },
  { src: "/assets/logo6.png", alt: "Client organization 6" },
];

const TrustLogoRow = () => (
  <section data-testid="trust-logo-section" className="bg-white border-t border-[#E8DCDD]">
    <div className="max-w-5xl mx-auto px-6 py-14">
      <div className="text-center mb-10">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">
          Social proof
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2628]">
          Trusted by leaders at these organizations
        </h2>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-6 sm:gap-8 items-center justify-items-center"
           data-testid="trust-logo-row">
        {SOCIAL_PROOF_LOGOS.map((logo, i) => (
          <img
            key={logo.src}
            src={logo.src}
            alt={logo.alt}
            data-testid={`trust-logo-${i + 1}`}
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="h-12 sm:h-14 w-auto object-contain opacity-80 hover:opacity-100 transition"
          />
        ))}
      </div>
    </div>
  </section>
);

const CourseTestimonials = ({ testimonials }) => {
  const items = Array.isArray(testimonials) ? testimonials : [];
  if (items.length === 0) return null;
  return (
    <section
      data-testid="course-testimonials-section"
      className="bg-white border-t border-[#E8DCDD]"
    >
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">
            Student stories
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2628]">
            What past students say about this course
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="course-testimonials-grid">
          {items.map((t, i) => (
            <figure
              key={i}
              data-testid={`course-testimonial-${i + 1}`}
              className="bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm p-6 relative"
            >
              <Quote className="absolute top-4 right-4 w-6 h-6 text-[#D9C5C9]" aria-hidden />
              <blockquote className="text-[15px] text-[#4A3E42] leading-relaxed italic pr-6">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-4 text-sm">
                <div className="font-semibold text-[#2D2628]" data-testid={`course-testimonial-${i + 1}-name`}>{t.name}</div>
                <div className="text-[#6B5E62]">
                  {[t.title, t.company].filter(Boolean).join(", ")}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

const SocialProofSection = ({ onEnroll }) => (
  <section
    data-testid="social-proof-section"
    className="bg-white border-t border-[#E8DCDD]"
  >
    <div className="max-w-5xl mx-auto px-6 py-14 text-center">
      <button
        type="button"
        onClick={onEnroll}
        data-testid="social-proof-cta"
        className="inline-flex items-center justify-center bg-[#8C4A57] text-white text-[13px] tracking-[0.14em] uppercase font-semibold px-8 py-4 rounded-sm hover:bg-[#6f3945] transition"
      >
        Enroll now — 7-day introductory offer
      </button>
      <p className="text-[11px] text-[#8C4A57] text-center mt-3 font-semibold flex items-center justify-center gap-1">
        Introductory price ends in <IntroCountdown compact />
      </p>
    </div>
  </section>
);

const PactMiniOffer = ({ course }) => {
  const bonus = course?.bonus_ebook;
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  if (!bonus) return null;
  const title = bonus.title || "Companion Ebook";
  const blurb = bonus.blurb || "";
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/payments/course-ebook-checkout`, {
        email,
        origin_url: window.location.href,
        course_id: course.id,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Could not start checkout");
        setLoading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };
  return (
    <section data-testid="course-ebook-offer" className="bg-[#0F1A2E] text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase bg-[#C9A961]/20 border border-[#C9A961]/40 rounded-sm px-3 py-1 mb-4 text-[#C9A961]">
            <Download className="w-3 h-3" /> Companion Ebook · $6.99
          </div>
          <h2 className="text-3xl sm:text-4xl font-serif font-semibold leading-tight mb-4"
              data-testid="course-ebook-title">
            Take {title} home for $6.99
          </h2>
          {blurb && (
            <p className="text-white/85 leading-relaxed max-w-xl mb-4">{blurb}</p>
          )}
          <ul className="text-white/70 text-sm space-y-1.5">
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#C9A961]" /> Instant PDF download after checkout</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#C9A961]" /> Lifetime access — a copy is emailed to you too</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#C9A961]" /> Pairs directly with this course</li>
          </ul>
        </div>
        <div className="md:col-span-5">
          <form onSubmit={submit} className="bg-white rounded-sm p-6" data-testid="course-ebook-form">
            <label className="block text-xs uppercase tracking-wider text-[#6B5E62] mb-2">Your email</label>
            <input
              required type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="course-ebook-email"
              className="w-full px-4 py-3 bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm focus:ring-2 focus:ring-[#D9C5C9] focus:outline-none text-sm mb-4 text-[#2D2628]" />
            <button
              type="submit" disabled={loading}
              data-testid="course-ebook-submit"
              className="w-full bg-[#8C4A57] text-white hover:bg-[#703B45] py-3 rounded-sm text-sm font-semibold uppercase tracking-wide transition-colors duration-200 disabled:opacity-60">
              {loading ? "Redirecting to checkout…" : "Get the ebook · $6.99"}
            </button>
            <p className="text-[10px] text-[#6B5E62] mt-3 text-center leading-relaxed">
              Secure checkout via Stripe · Delivery to the email above
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

const Section = ({ label, title, children, alt = false, id }) => (
  <section id={id} className={`py-14 ${alt ? "bg-[#FAF4F5]" : "bg-white"}`}>
    <div className="max-w-5xl mx-auto px-6">
      {label && <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">{label}</div>}
      {title && <h2 className="font-serif text-2xl sm:text-3xl text-[#2D2628] mb-6">{title}</h2>}
      {children}
    </div>
  </section>
);

export default function CourseDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [settings, setSettings] = useState(null);
  const [modules, setModules] = useState([]);
  const [bundleCourses, setBundleCourses] = useState([]); // [{product, modules[]}]
  const [openBundleCourse, setOpenBundleCourse] = useState(null); // productId or null
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    Promise.all([
      axios.get(`${API}/products/${productId}`),
      axios.get(`${API}/courses/${productId}/settings`),
      axios.get(`${API}/courses/${productId}/modules`).catch(() => ({ data: { modules: [] } })),
    ])
      .then(([p, s, m]) => {
        setProduct(p.data);
        setSettings(s.data);
        setModules(Array.isArray(m.data) ? m.data : m.data?.modules || []);
        // For bundle products, also fetch each member course + its modules so
        // shoppers can drill into exactly what's inside.
        const ids = p.data?.course_ids || [];
        if (ids.length > 0) {
          Promise.all(ids.map(cid =>
            Promise.all([
              axios.get(`${API}/products/${cid}`).catch(() => null),
              axios.get(`${API}/courses/${cid}/modules`).catch(() => ({ data: { modules: [] } })),
            ]).then(([pr, mr]) => ({
              product: pr?.data,
              modules: mr.data?.modules || (Array.isArray(mr.data) ? mr.data : []),
            }))
          )).then(list => setBundleCourses(list.filter(x => x.product)));
        }
      })
      .catch((e) => setError(e.response?.data?.detail || "Course not found"));
    window.scrollTo(0, 0);
  }, [productId]);

  useMeta({
    title: product ? `${product.title} — Global Leader Solutions` : "Course",
    description: settings?.description || product?.short_desc || "Character-based leadership course.",
    canonical: product ? `https://globalleadersolutions.com/courses/${product.id}` : undefined,
    image: product?.cover ? (product.cover.startsWith("/") ? `https://globalleadersolutions.com${product.cover}` : product.cover) : undefined,
    jsonLd: product ? {
      "@context": "https://schema.org",
      "@type": "Course",
      name: product.title,
      description: settings?.description || product.short_desc || product.title,
      provider: {
        "@type": "Organization",
        name: "Global Leader Solutions",
        sameAs: "https://globalleadersolutions.com",
      },
      url: `https://globalleadersolutions.com/courses/${product.id}`,
      image: product.cover?.startsWith("/") ? `https://globalleadersolutions.com${product.cover}` : product.cover,
      offers: {
        "@type": "Offer",
        price: product.price,
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: `https://globalleadersolutions.com/cart?product=${product.id}`,
      },
      ...(settings?.length_hours ? { timeRequired: `PT${Math.round(settings.length_hours * 60)}M` } : {}),
      ...(settings?.learning_objectives?.length ? {
        teaches: settings.learning_objectives,
      } : {}),
      ...(settings?.who_for?.length ? {
        audience: { "@type": "Audience", audienceType: settings.who_for.join("; ") },
      } : {}),
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: "online",
        courseWorkload: settings?.length_hours ? `PT${Math.round(settings.length_hours * 60)}M` : undefined,
      },
    } : undefined,
  });

  if (error) return (
    <div className="max-w-2xl mx-auto py-24 px-6 text-center">
      <p className="text-[#8C4A57] mb-4">{error}</p>
      <Link to="/courses" className="text-[#8C4A57] underline">Return to all courses</Link>
    </div>
  );
  if (!product || !settings) return <div className="py-24 text-center text-[#6B5E62]">Loading…</div>;

  const savings = product.regular_price && product.regular_price > product.price
    ? product.regular_price - product.price
    : 0;
  const inc = settings.inclusions || {};
  const isIntroOffer = product.id === "c_conflict_3";

  // Add course to cart, notify, and take them to checkout.
  const enroll = () => {
    addItem(product);
    toast.success(`${product.title} added to cart`);
    navigate("/cart");
  };

  return (
    <div className="bg-white text-[#2D2628]" data-testid={`course-detail-${product.id}`}>
      {/* Hero */}
      <div className="bg-gradient-to-b from-[#FAF4F5] to-white pb-16 pt-12">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/courses" className="inline-flex items-center gap-1 text-[11px] tracking-widest uppercase text-[#8C4A57] mb-6 hover:underline"><ArrowLeft className="w-3 h-3"/> All courses</Link>
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-10 items-start">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">{product.category_label} · Course</div>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2D2628] leading-tight mb-4">{product.title}</h1>
              {product.short_desc && <p className="text-lg text-[#4A3E42] leading-relaxed mb-6">{product.short_desc}</p>}
              {settings.length_hours && (
                <div className="inline-flex items-center gap-2 text-[13px] text-[#4A3E42] bg-white border border-[#E8DCDD] rounded-full px-4 py-1.5"><Clock className="w-4 h-4 text-[#8C4A57]"/>{settings.length_hours} hours of instruction</div>
              )}
            </div>
            <aside className="bg-white border border-[#E8DCDD] rounded-sm p-6 shadow-md" data-testid="course-price-card">
              <div className="flex items-baseline gap-3 mb-1">
                {savings && <span className="text-[#6B5E62] line-through text-lg" data-testid="course-regular-price">{priceUSD(product.regular_price)}</span>}
                <span className="font-serif text-4xl text-[#2D2628]" data-testid="course-price">{priceUSD(product.price)}</span>
              </div>
              {isIntroOffer ? (
                <div className="space-y-2 mb-4" data-testid="intro-offer-block">
                  <div className="inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase font-semibold text-[#8C4A57] bg-[#F7E2E5] border border-[#E8DCDD] rounded-sm px-2 py-1"
                       data-testid="intro-offer-badge">
                    7-Day Introductory Offer
                  </div>
                  <p className="text-[12px] text-[#8C4A57] font-semibold">
                    Introductory price — ends soon.
                  </p>
                  <IntroCountdown />
                </div>
              ) : savings > 0 ? (
                <div className="mb-4 inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase font-semibold text-[#8C4A57] bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm px-2 py-1"
                     data-testid="course-discount-badge">
                  Save {priceUSD(savings)} — {Math.round((savings / product.regular_price) * 100)}% off
                </div>
              ) : (
                <div className="mb-4" />
              )}
              <ul className="space-y-2 mb-6 text-sm text-[#4A3E42]">
                {inc.ebook && <li className="flex gap-2"><FileText className="w-4 h-4 text-[#8C4A57] flex-shrink-0 mt-0.5"/> Includes ebook</li>}
                {inc.workbook && <li className="flex gap-2"><BookOpen className="w-4 h-4 text-[#8C4A57] flex-shrink-0 mt-0.5"/> Includes workbook</li>}
                {inc.audio && <li className="flex gap-2"><Music className="w-4 h-4 text-[#8C4A57] flex-shrink-0 mt-0.5"/> Includes audio (MP3)</li>}
                {inc.supplement && <li className="flex gap-2"><FileText className="w-4 h-4 text-[#8C4A57] flex-shrink-0 mt-0.5"/> Supplemental readings</li>}
                {inc.certificate && <li className="flex gap-2"><Award className="w-4 h-4 text-[#8C4A57] flex-shrink-0 mt-0.5"/> Signed Certificate of Completion</li>}
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#8C4A57] flex-shrink-0 mt-0.5"/> Lifetime access · Self-paced</li>
              </ul>
              {isIntroOffer && (
                <VideoEmbed url='https://youtu.be/BZdjFV6pfKk' title='Navigating Organizational Conflict to Organizational Clarity' />
              )}
              {product.id === 'c_leadership' && (
                <VideoEmbed url='https://youtu.be/W_oBwYDin2k' title='Character Code of Self-Leadership' />
              )}
              <button
                type="button"
                onClick={enroll}
                data-testid="course-cta-buy"
                className="block w-full text-center bg-[#8C4A57] text-white text-[13px] tracking-[0.14em] uppercase font-semibold px-6 py-3.5 rounded-sm hover:bg-[#6f3945] transition"
              >
                Enroll now
              </button>
              {isIntroOffer && (
                <p className="text-[11px] text-[#8C4A57] text-center mt-2 font-semibold" data-testid="intro-offer-under-cta">
                  Introductory price ends in <IntroCountdown compact />
                </p>
              )}
              <p className="text-[10px] text-[#6B5E62] text-center mt-3 flex items-center justify-center gap-1"><Lock className="w-3 h-3"/> Secure checkout · Instant access</p>
            </aside>
          </div>
        </div>
      </div>

      {isIntroOffer && (
        <>
          <SocialProofSection onEnroll={enroll} />
        </>
      )}
      <TrustLogoRow />
      <CourseTestimonials testimonials={product.testimonials} />
      <PactMiniOffer course={product} />

      {settings.narrative && (
        <Section label="About this course">
          <p className="text-[15px] text-[#4A3E42] leading-loose whitespace-pre-line">{settings.narrative}</p>
        </Section>
      )}

      {settings.description && (
        <Section label="Course description" alt id="description">
          <p className="text-[15px] text-[#4A3E42] leading-loose whitespace-pre-line" data-testid="course-description">{settings.description}</p>
        </Section>
      )}

      {settings.why_take && (
        <Section label="Why you'll want to take this course" id="why-take">
          <p className="text-[15px] text-[#4A3E42] leading-loose whitespace-pre-line" data-testid="course-why-take">{settings.why_take}</p>
        </Section>
      )}

      {settings.who_for.length > 0 && (
        <Section label="Is this course for you? Absolutely, if you are" alt id="who-for">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="course-who-for-list">
            {settings.who_for.map((w, i) => (
              <li key={i} data-testid={`who-for-${i}`} className="flex gap-3 items-start"><span className="text-[#8C4A57] mt-1">•</span><span className="text-[14px] text-[#4A3E42]">{w}</span></li>
            ))}
          </ul>
        </Section>
      )}

      {settings.why_choose.length > 0 && (
        <Section label="Why choose this course" id="why-choose">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="course-why-choose-list">
            {settings.why_choose.map((w, i) => (
              <div key={i} data-testid={`why-choose-${i}`} className="border-t-2 border-[#8C4A57] pt-4">
                <p className="text-[14px] text-[#4A3E42] leading-relaxed">{w}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {settings.learning_objectives.length > 0 && (
        <Section label="Course learning objectives"
                 title="What you will walk away knowing how to do"
                 alt id="learning-objectives">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {settings.learning_objectives.map((o, i) => (
              <li key={i} data-testid={`obj-${i}`} className="flex gap-3 border border-[#E8DCDD] rounded-sm p-4 bg-white">
                <CheckCircle2 className="w-5 h-5 text-[#8C4A57] flex-shrink-0 mt-0.5"/>
                <span className="text-[14px] text-[#2D2628] leading-relaxed">{o}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {bundleCourses.length > 0 && (
        <Section label={`What's inside this bundle — ${bundleCourses.length} courses`}
                 title="Every course, unlocked in one purchase"
                 id="whats-inside">
          <div className="space-y-3" data-testid="bundle-course-list">
            {bundleCourses.map(({ product: bp, modules: bmods }) => {
              const open = openBundleCourse === bp.id;
              return (
                <div
                  key={bp.id}
                  data-testid={`bundle-course-${bp.id}`}
                  className={`border rounded-sm bg-white overflow-hidden transition-colors ${open ? "border-[#8C4A57]" : "border-[#E8DCDD]"}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenBundleCourse(open ? null : bp.id)}
                    data-testid={`bundle-course-toggle-${bp.id}`}
                    aria-expanded={open}
                    className="w-full flex items-center gap-4 p-5 text-left hover:bg-[#FAF4F5]/60 transition-colors"
                  >
                    <span className="flex-shrink-0 w-11 h-11 rounded-sm bg-[#FAF4F5] border border-[#E8DCDD] overflow-hidden">
                      {bp.cover && (
                        <img
                          src={bp.cover.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${bp.cover}` : bp.cover}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[9px] tracking-[0.24em] uppercase text-[#8C4A57] mb-0.5">{bp.category_label}</div>
                      <div className="font-serif text-[16px] text-[#2D2628] leading-tight">{bp.title}</div>
                      <div className="text-[11px] text-[#6B5E62] mt-1">{bmods.length} module{bmods.length === 1 ? "" : "s"}{bp.short_desc ? ` · ${bp.short_desc}` : ""}</div>
                    </div>
                    <span className={`text-[#8C4A57] flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
                  </button>
                  {open && (
                    <div className="border-t border-[#E8DCDD] p-5 bg-[#FAF4F5]/40">
                      {bmods.length > 0 ? (
                        <ol className="space-y-2">
                          {bmods.map((m, i) => (
                            <li key={m.id || i} className="flex gap-3 items-start">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white border border-[#E8DCDD] text-[#8C4A57] font-serif text-xs flex items-center justify-center">
                                {i + 1}
                              </span>
                              <div className="min-w-0">
                                <div className="text-[13px] font-serif text-[#2D2628]">{m.title}</div>
                                {m.description && (
                                  <div className="text-[11px] text-[#4A3E42] mt-0.5 leading-relaxed whitespace-pre-line">{m.description}</div>
                                )}
                              </div>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <div className="text-[12px] text-[#6B5E62] italic">Curriculum coming soon.</div>
                      )}
                      <Link
                        to={`/courses/${bp.id}`}
                        className="inline-block mt-4 text-[10px] tracking-[0.14em] uppercase font-semibold text-[#8C4A57] hover:underline"
                        data-testid={`bundle-course-link-${bp.id}`}
                      >
                        See full course page →
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {modules.length > 0 && (
        <Section label="Course curriculum"
                 title={`${modules.length} module${modules.length === 1 ? "" : "s"} — self-paced`}
                 id="curriculum">
          <ol className="space-y-3" data-testid="course-curriculum-list">
            {modules.map((m, i) => (
              <li
                key={m.id || i}
                data-testid={`curriculum-module-${i}`}
                className="border border-[#E8DCDD] bg-white rounded-sm p-5 flex gap-5 items-start hover:border-[#8C4A57] transition-colors"
              >
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[#FAF4F5] text-[#8C4A57] font-serif text-lg flex items-center justify-center border border-[#E8DCDD]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h3 className="font-serif text-[17px] text-[#2D2628]" data-testid={`curriculum-title-${i}`}>{m.title}</h3>
                    <PlayCircle className="w-4 h-4 text-[#8C4A57]" />
                  </div>
                  {m.description && (
                    <p className="text-[13px] text-[#4A3E42] leading-relaxed mt-1.5 whitespace-pre-line">{m.description}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Final CTA */}
      <section className="bg-[#0F1A2E] text-white py-16">
        <div className="max-w-2xl mx-auto text-center px-6">
          <h2 className="font-serif text-3xl mb-3">Ready to enroll?</h2>
          <p className="text-white/70 mb-8">{priceUSD(product.price)}{savings ? ` (regularly ${priceUSD(product.regular_price)})` : ""} · Lifetime access · 30-day guarantee</p>
          <button
            type="button"
            onClick={enroll}
            data-testid="course-cta-buy-bottom"
            className="inline-block bg-[#C9A961] text-[#0F1A2E] text-[13px] tracking-[0.14em] uppercase font-semibold px-10 py-4 rounded-sm hover:bg-[#b8974f] transition"
          >
            Enroll now
          </button>
        </div>
      </section>
    </div>
  );
}
